// ============================================================================
// Booking Types
// ============================================================================

/**
 * Status of a booking
 */
export enum BookingStatus {
  Pending = 'Pending',
  Confirmed = 'Confirmed',
  InProgress = 'InProgress',
  Completed = 'Completed',
  Cancelled = 'Cancelled',
  NoShow = 'NoShow'
}

/**
 * Detail DTO for a service attached to a booking
 */
export interface BookingServiceDetailDto {
  serviceId: number;
  serviceName: string;
  serviceCategory: string;
  durationMinutes: number;
  priceCharged: number;
}

/**
 * Service reference for creating a booking
 */
export interface BookingServiceDto {
  serviceId: number;
}

/**
 * Full booking entity with all details
 */
export interface Booking {
  id: number;
  bookingDateTime: string;
  durationMinutes: number;
  status: BookingStatus;
  notes?: string;
  cancellationReason?: string;
  cancelledAt?: string;
  createdAt: string;
  updatedAt: string;
  userId: string;
  userName: string;
  userEmail: string;
  barberId: number;
  barberUserId?: string;
  barberName: string;
  barbershopId: number;
  barbershopName: string;
  barbershopAddress: string;
  services: BookingServiceDetailDto[];
  totalPrice: number;
  dayDifferentialAmount?: number;
  dayDifferentialDescription?: string;
  checkedInAt?: string;
  completedAt?: string;
  onTheWayAt?: string;
  estimatedMinutesAway?: number;
  // Tip (post-completion gratuity)
  tipAmount?: number;
  tipPaid?: boolean;
  tipPaidAt?: string;
  // Style Reference (Visual History style library)
  styleLibraryItemId?: number;
  styleReferenceName?: string;
  styleReferencePhotoUrl?: string;
  styleReferenceInstructions?: string;
  /**
   * The barbershop's IANA zone (e.g. "America/New_York") - bookingDateTime is an instant,
   * and the API reads a bare wall clock sent back as local to this zone. Absent from an
   * API that predates TZ-1c, null when unresolved: fall back to the browser's zone.
   */
  barbershopTimeZone?: string | null;
}

/**
 * DTO for booking responses (same structure as Booking)
 */
export interface BookingDto {
  id: number;
  bookingDateTime: string;
  durationMinutes: number;
  status: BookingStatus;
  notes?: string;
  cancellationReason?: string;
  cancelledAt?: string;
  createdAt: string;
  updatedAt: string;
  userId: string;
  userName: string;
  userEmail: string;
  barberId: number;
  barberUserId?: string;
  barberName: string;
  barbershopId: number;
  barbershopName: string;
  barbershopAddress: string;
  services: BookingServiceDetailDto[];
  totalPrice: number;
  dayDifferentialAmount?: number;
  dayDifferentialDescription?: string;
  checkedInAt?: string;
  completedAt?: string;
  onTheWayAt?: string;
  estimatedMinutesAway?: number;
  // Tip (post-completion gratuity)
  tipAmount?: number;
  tipPaid?: boolean;
  tipPaidAt?: string;
  // Style Reference (Visual History style library)
  styleLibraryItemId?: number;
  styleReferenceName?: string;
  styleReferencePhotoUrl?: string;
  styleReferenceInstructions?: string;
  /**
   * The barbershop's IANA zone (e.g. "America/New_York") - bookingDateTime is an instant,
   * and the API reads a bare wall clock sent back as local to this zone. Absent from an
   * API that predates TZ-1c, null when unresolved: fall back to the browser's zone.
   */
  barbershopTimeZone?: string | null;
}

/**
 * Simple DTO for selecting services in a booking request
 */
export interface ServiceSelectionDto {
  serviceId: number;
}

/**
 * Request DTO for creating a new booking
 */
export interface CreateBookingDto {
  bookingDateTime: string;
  barberId: number;
  barbershopId: number;
  services: ServiceSelectionDto[];
  notes?: string;
  addOnServiceKeys?: string[];  // Optional add-on selection
  useMembership?: boolean;  // Optional membership usage flag
  /**
   * Optional reference to a style from the customer's style library
   * to attach to this booking (must belong to the requesting customer).
   */
  styleLibraryItemId?: number;
}

// ============================================================================
// On The Way and My Day Types
// ============================================================================

/**
 * Request DTO for marking customer as on the way
 */
export interface MarkOnTheWayDto {
  estimatedMinutesAway?: number;
}

/**
 * Response DTO for on the way action
 */
export interface OnTheWayResponse {
  success: boolean;
  message: string;
  onTheWayAt?: string;
  bookingId: number;
  estimatedMinutesAway?: number;
}

/**
 * DTO for barber's daily schedule and statistics
 */
export interface BarberMyDay {
  barberId: number;
  barberName: string;
  date: string;
  totalBookings: number;
  completedCount: number;
  remainingCount: number;
  expectedRevenue: number;
  completedRevenue: number;
  onTheWayBookings: BookingDto[];
  checkedInBookings: BookingDto[];
  upcomingBookings: BookingDto[];
  inProgressBookings: BookingDto[];
  completedBookings: BookingDto[];
  noShowBookings: BookingDto[];
  /**
   * IANA zone of the shop whose day this is (`date` is that shop's calendar date).
   * Absent from an API that predates TZ-1c, null when unresolved.
   */
  timeZone?: string | null;
}

/**
 * DTO for barber's service offering in booking context
 * Note: For detailed service management, use BarberServiceDto from service.ts
 */
export interface BookingServiceDto {
  serviceId: number;
  serviceName: string;
  serviceCategory: string;
  durationMinutes: number;
  price: number;
  isActive: boolean;
}
